import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../../config/firebase';
import { useOrgId } from '../../context/OrgContext';
import { useOrgSettings } from '../../hooks/useOrgSettings';
import HelpDrawer from '../Help';

const NavItem = ({ to, children, onNav }) => (
  <NavLink
    to={to}
    onClick={onNav}
    className={({ isActive }) =>
      `block px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
        isActive
          ? 'bg-blue-600 text-white'
          : 'text-slate-300 hover:bg-slate-700 hover:text-white'
      }`
    }
  >
    {children}
  </NavLink>
);

const Layout = ({ user, children, availableOrgs = [], onSwitchOrg }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [reportsOpen, setReportsOpen] = useState(true);
  const [adminOpen, setAdminOpen]     = useState(false);
  const [helpOpen, setHelpOpen]       = useState(false);
  const orgId = useOrgId();
  const { orgName, logoUrl } = useOrgSettings(orgId);

  const closeSidebar = () => setSidebarOpen(false);

  const sidebarContent = (
    <>
      {/* Logo / org name */}
      <div className="px-4 py-5 border-b border-slate-700 flex-shrink-0">
        <div className="flex items-center gap-2">
          {logoUrl ? (
            <img src={logoUrl} alt="logo" className="w-8 h-8 rounded-lg object-contain bg-white" />
          ) : (
            <svg viewBox="0 0 72 72" className="w-8 h-8 flex-shrink-0" xmlns="http://www.w3.org/2000/svg">
              <rect x="0" y="0" width="72" height="72" rx="16" fill="#2C2C2A"/>
              <rect x="25" y="20" width="22" height="6" rx="3" fill="#854F0B"/>
              <rect x="18" y="31" width="36" height="7" rx="3" fill="#BA7517"/>
              <rect x="10" y="43" width="52" height="10" rx="3" fill="#EF9F27"/>
            </svg>
          )}
          <div className="min-w-0">
            <div className="text-white text-sm font-bold leading-tight truncate">{orgName || 'Blessio'}</div>
            <div className="text-slate-400 text-xs leading-tight">Books</div>
          </div>
        </div>
      </div>

      {/* Nav links */}
      <nav className="flex-1 px-2 py-4 space-y-0.5 overflow-y-auto">
        <NavItem to="/" onNav={closeSidebar}>Home</NavItem>
        <NavItem to="/transactions" onNav={closeSidebar}>Transactions</NavItem>
        <NavItem to="/reconcile" onNav={closeSidebar}>Reconcile</NavItem>

        <div>
          <button
            onClick={() => setReportsOpen(o => !o)}
            aria-expanded={reportsOpen}
            className="w-full text-left px-3 py-2 rounded-lg text-sm font-medium text-slate-300 hover:bg-slate-700 hover:text-white transition-colors flex items-center justify-between"
          >
            <span>Reports</span>
            <span className="text-slate-500 text-xs">{reportsOpen ? '▾' : '▸'}</span>
          </button>
          {reportsOpen && (
            <div className="pl-3 mt-0.5 space-y-0.5">
              <NavItem to="/reports/income-expense" onNav={closeSidebar}>Cash Flow</NavItem>
              <NavItem to="/reports/balance-sheet" onNav={closeSidebar}>Account Balances</NavItem>
              <NavItem to="/reports/budget" onNav={closeSidebar}>Budget by Month</NavItem>
              <NavItem to="/reports/payees" onNav={closeSidebar}>Payees</NavItem>
              <NavItem to="/reports/export" onNav={closeSidebar}>Export</NavItem>
            </div>
          )}
        </div>

        {user?.isAdmin && (
          <div>
            <button
              onClick={() => setAdminOpen(o => !o)}
              aria-expanded={adminOpen}
              className="w-full text-left px-3 py-2 rounded-lg text-sm font-medium text-slate-400 hover:bg-slate-700 hover:text-white transition-colors flex items-center justify-between mt-2"
            >
              <span>Admin</span>
              <span className="text-slate-500 text-xs">{adminOpen ? '▾' : '▸'}</span>
            </button>
            {adminOpen && (
              <div className="pl-3 mt-0.5 space-y-0.5">
                <NavItem to="/admin" onNav={closeSidebar}>Settings</NavItem>
                <NavItem to="/reports/1099" onNav={closeSidebar}>1099 Prep</NavItem>
              </div>
            )}
          </div>
        )}

        {user?.isSuperAdmin && (
          <div className="mt-2 pt-2 border-t border-slate-700">
            <NavItem to="/manager" onNav={closeSidebar}>Manager</NavItem>
          </div>
        )}

        <div className="mt-2 pt-2 border-t border-slate-700">
          <button
            onClick={() => { setHelpOpen(true); closeSidebar(); }}
            className="w-full text-left px-3 py-2 rounded-lg text-sm font-medium text-slate-300 hover:bg-slate-700 hover:text-white transition-colors"
          >
            Help
          </button>
        </div>
      </nav>

      {/* Footer */}
      <div className="px-2 py-3 border-t border-slate-700 flex-shrink-0">
        <div className="text-slate-500 text-xs px-3 mb-1 truncate">{user?.email}</div>
        {availableOrgs.length > 1 && (
          <button
            onClick={onSwitchOrg}
            className="w-full text-left px-3 py-2 rounded-lg text-sm text-slate-400 hover:bg-slate-700 hover:text-white transition-colors"
          >
            Switch Org
          </button>
        )}
        <button
          onClick={() => signOut(auth)}
          className="w-full text-left px-3 py-2 rounded-lg text-sm text-slate-400 hover:bg-slate-700 hover:text-white transition-colors"
        >
          Sign Out
        </button>
      </div>
    </>
  );

  return (
    <div className="flex h-screen bg-gray-100 overflow-hidden">

      {/* ── Mobile overlay sidebar ── */}
      {sidebarOpen && (
        <div className="md:hidden fixed inset-0 z-40 flex">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/50" onClick={closeSidebar} />
          {/* Drawer */}
          <aside className="relative z-50 w-64 bg-slate-800 flex flex-col h-full shadow-2xl">
            {sidebarContent}
          </aside>
        </div>
      )}

      {/* ── Desktop sidebar (always visible) ── */}
      <aside className="hidden md:flex w-52 bg-slate-800 flex-col flex-shrink-0">
        {sidebarContent}
      </aside>

      {/* ── Main content ── */}
      <div className="flex-1 flex flex-col overflow-hidden">

        {/* Mobile top bar */}
        <header className="md:hidden bg-slate-800 px-4 py-3 flex items-center gap-3 flex-shrink-0">
          <button
            onClick={() => setSidebarOpen(true)}
            className="text-slate-300 hover:text-white p-1 -ml-1"
            aria-label="Open menu"
            title="Open menu"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          {logoUrl ? (
            <img src={logoUrl} alt="logo" className="w-6 h-6 rounded object-contain bg-white" />
          ) : (
            <svg viewBox="0 0 72 72" className="w-6 h-6 flex-shrink-0" xmlns="http://www.w3.org/2000/svg">
              <rect x="0" y="0" width="72" height="72" rx="16" fill="#2C2C2A"/>
              <rect x="25" y="20" width="22" height="6" rx="3" fill="#854F0B"/>
              <rect x="18" y="31" width="36" height="7" rx="3" fill="#BA7517"/>
              <rect x="10" y="43" width="52" height="10" rx="3" fill="#EF9F27"/>
            </svg>
          )}
          <span className="text-white text-sm font-bold truncate">{orgName || 'Blessio'}</span>
        </header>

        <main className="flex-1 overflow-y-auto">
          {children}
        </main>
      </div>

      {helpOpen && <HelpDrawer onClose={() => setHelpOpen(false)} />}
    </div>
  );
};

export default Layout;
